/**
 * 実績トレンド反映の集計窓 W (コマ数) を端末単位で永続化する util
 * (demand-trend-window-variable design D4)。
 *
 * Decision D4: 永続化方式は flat な localStorage key (org/area/section スコープ
 * 無し、`SpotBidding/models/sidebarStorage.ts` 型の薄い best-effort util)。W は
 * 個人の集計嗜好で断面横断が自然なため、スコープ分割はしない。
 *
 * proto 非依存の plain な number を出し入れするだけの表示専用の一時状態
 * (avgRatio/corrected_slots の再計算そのものは backend RPC が担う)。
 */

/** AC-DFD-009a: 集計窓は 0.5〜12h = 1〜24 コマ。 */
export const TREND_WINDOW_KOMA_MIN = 1
export const TREND_WINDOW_KOMA_MAX = 24
/** D3: 既定 3h = 6 コマ。 */
export const TREND_WINDOW_KOMA_DEFAULT = 6

export const TREND_WINDOW_STORAGE_KEY = 'ebalance:demandForecastTrendWindowKoma'

/**
 * n を集計窓の値域 (1〜24 コマ) にクランプする。 小数は四捨五入してから
 * クランプする (design HTML `dft-trend-koma` の `Math.round` + clamp と同じ
 * 挙動)。
 */
export const clampTrendWindowKoma = (n: number): number =>
  Math.min(
    TREND_WINDOW_KOMA_MAX,
    Math.max(TREND_WINDOW_KOMA_MIN, Math.round(n)),
  )

/**
 * 集計窓 W を localStorage に保存する (即時永続化、 UI ブロックなし)。
 * quota 超過 / private mode 等の保存不可は黙って諦める (best-effort、
 * `SpotBidding/models/sidebarStorage.ts` と同方針)。
 */
export const saveTrendWindowKoma = (koma: number): void => {
  try {
    localStorage.setItem(
      TREND_WINDOW_STORAGE_KEY,
      String(clampTrendWindowKoma(koma)),
    )
  } catch {
    // quota 超過 / private mode などは黙って諦める (W は次回も既定値に戻るだけ)。
  }
}

/**
 * 集計窓 W を読み出す。 未保存 / パース不能 (非数) は既定値 (6コマ) へ
 * フォールバックする。 範囲外の保存値は端 (1〜24) にクランプする
 * (`clampTrendWindowKoma` と同じ挙動 — 過去の実装変更等で範囲外の値が
 * 保存されていても壊れず復元できる)。
 */
export const loadTrendWindowKoma = (): number => {
  let raw: string | null
  try {
    raw = localStorage.getItem(TREND_WINDOW_STORAGE_KEY)
  } catch {
    return TREND_WINDOW_KOMA_DEFAULT
  }
  if (raw === null || raw.trim() === '') return TREND_WINDOW_KOMA_DEFAULT
  const n = Number(raw)
  if (!Number.isFinite(n)) return TREND_WINDOW_KOMA_DEFAULT
  return clampTrendWindowKoma(n)
}
